import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaChalkboardTeacher, FaTrophy, FaChild, FaClock } from 'react-icons/fa';
import { useSeason } from '../contexts/SeasonContext';

import Header from '../components/Header';
import Gallery from '../components/Gallery';
import CurrentEvents from '../components/CurrentEvents';
import Partenaires from '../components/Partenaires';
import ScrollReveal, { StaggerReveal, StaggerItem } from '../components/ScrollReveal';
import AnimatedCounter from '../components/AnimatedCounter';

import imgTeam       from '../images/home/team.jpg';
import imgCourts     from '../images/home/badmintoncourt.jpg';
import imgTournament from '../images/home/tournament.jpg';
import imgYoungs     from '../images/home/youngTeam.jpg';
import heroBg        from '../images/home/hero-bg.jpg';
import heroBgMobile  from '../images/home/hero-bg-mobile.jpg';
import { publicApi } from '../api';
import { trackInscriptionClick } from '../utils/analytics';

/* ── Fallback statique si l'API est indisponible ─────── */
const STATIC_GALLERY = [
  { src: imgCourts,     alt: 'Terrain de badminton' },
  { src: imgTournament, alt: 'Tournoi de badminton' },
  { src: imgYoungs,     alt: 'Jeunes joueurs de badminton' },
  { src: imgTeam,       alt: 'Groupe de joueurs' },
];

/* ── Data ────────────────────────────────────────────── */
const stats = [
  { value: 1998, suffix: '',    label: 'Année de fondation', desc: 'Plus de 25 ans de passion' },
  { value: 7,    suffix: '',    label: 'Terrains de jeu',    desc: 'Aux normes internationales' },
  { value: 200,  suffix: '+',   label: 'Membres actifs',     desc: 'Toute l\'année' },
  { value: null, label: 'Tous niveaux', desc: 'Débutant à confirmé' },
];

const features = [
  { icon: FaUsers,             title: 'Entraînements adaptés',  desc: 'Des coachs qualifiés pour progresser à votre rythme, quel que soit votre niveau.' },
  { icon: FaChalkboardTeacher, title: 'Ambiance conviviale',    desc: 'Rencontrez des passionnés de badminton et créez des liens durables.' },
  { icon: FaTrophy,            title: 'Compétitions & loisirs', desc: 'Tournois passionnants ou jeu libre : le badminton selon vos envies.' },
  { icon: FaChild,             title: 'Installations modernes', desc: '7 terrains bien entretenus, ouverts du lundi au samedi.' },
  { icon: FaClock,             title: 'Horaires flexibles',     desc: 'Nombreux créneaux pour s\'adapter à votre emploi du temps.' },
];

/* ── Hero Background ─────────────────────────────────── */
const HeroBackground = () => (
  <div className="hidden md:block absolute inset-0 overflow-hidden" aria-hidden="true">
    <img src={heroBg} alt="" className="absolute inset-0 w-full h-full object-cover object-right" />
    <div className="absolute inset-0" style={{
      background: 'linear-gradient(95deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.25) 45%, transparent 65%)',
    }} />
  </div>
);

/* ── Feature Row ─────────────────────────────────────── */
const FeatureItem = ({ icon: Icon, title, desc }) => (
  <StaggerItem className="flex gap-5 items-start p-6 rounded-2xl hover:bg-stone-50 transition-colors group">
    <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-800 shrink-0 group-hover:bg-brand-800 group-hover:text-white transition-all duration-300">
      <Icon size={18} />
    </div>
    <div>
      <h3 className="font-semibold text-stone-950 mb-1 text-base">{title}</h3>
      <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
    </div>
  </StaggerItem>
);

/* ── Page ────────────────────────────────────────────── */
const Accueil = ({ setCurrentSection, currentSection, setActiveTab }) => {
  const { season, loading: seasonLoading, seasonLabel } = useSeason();
  const inscriptionsOpen = season?.inscriptions_open ?? true;
  const [galleryImages, setGalleryImages] = useState(STATIC_GALLERY);

  useEffect(() => {
    publicApi.get('gallery')
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setGalleryImages(data.map(img => ({ src: img.image_url, alt: img.alt_text || '', title: img.title || '' })));
        }
      })
      .catch(() => {}); // garde les images statiques en cas d'erreur
  }, []);

  return (
    <div>
      {/* ═══════════════════════════════════════════ HERO */}
      <section className="relative min-h-screen bg-white flex flex-col">
        <HeroBackground />

        <Header setCurrentSection={setCurrentSection} currentSection={currentSection} />

        <div className="relative z-10 flex-1 flex flex-col">

          {/* Texte centré desktop / aligné haut mobile */}
          <div className="flex-1 flex items-center">
            <div className="max-w-6xl mx-auto px-5 w-full pt-20 pb-6 md:pb-10">
              <div className="max-w-2xl">
                {!seasonLoading && season && (
                  <div className="inline-flex items-center gap-2 border border-stone-200 rounded-full px-4 py-1.5 mb-8 bg-white/80 backdrop-blur-sm animate-fadeUp">
                    <span className={`w-1.5 h-1.5 rounded-full ${inscriptionsOpen ? 'bg-green-500 animate-pulseDot' : 'bg-red-500'}`} />
                    <span className="text-xs font-medium text-stone-500 tracking-wide">
                      {season.description} — {inscriptionsOpen ? 'Inscriptions ouvertes' : 'Inscriptions fermées'}
                    </span>
                  </div>
                )}

                <h1 className="display-xl mb-6 animate-fadeUp [animation-delay:100ms]">
                  Badminton<br />
                  <span className="gradient-brand-text">Club</span><br />
                  Éragny
                </h1>

                <p className="text-lg text-stone-500 leading-relaxed mb-10 max-w-md animate-fadeUp [animation-delay:200ms]">
                  Un club convivial à Éragny-sur-Oise, ouvert à tous les niveaux.
                  Venez vivre votre passion du badminton avec nous.
                </p>

                <div className="flex flex-wrap gap-3 animate-fadeUp [animation-delay:300ms]">
                  <Link to="/inscription" onClick={() => { setCurrentSection('inscription'); trackInscriptionClick(); }} className="btn-brand">
                    Nous rejoindre
                  </Link>
                  <Link to="/club" onClick={() => setCurrentSection('club')} className="btn-ghost">
                    Découvrir le club
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Image mobile dans le flux : sous le texte, jamais superposée */}
          <div className="md:hidden relative overflow-hidden shrink-0 h-[38dvh]">
            <img src={heroBgMobile} alt="" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white to-transparent pointer-events-none" />
          </div>

        </div>

        <div className="relative z-10 flex flex-col items-center gap-2 pb-8 animate-fadeUp [animation-delay:500ms]">
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-300">Découvrir</span>
          <div className="w-px h-10 overflow-hidden rounded-full bg-stone-100">
            <div className="w-full h-full animate-scrollLine" style={{ background: 'var(--gradient-brand)' }} />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════ STATS */}
      <section className="bg-stone-950">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-stone-800">
            {stats.map((s, i) => (
              <ScrollReveal key={i} delay={i * 0.05} className="p-8 text-center">
                <div className="text-4xl font-extrabold tracking-tighter text-white mb-1.5 tabular-nums">
                  {typeof s.value === 'number'
                    ? <AnimatedCounter target={s.value} suffix={s.suffix} />
                    : <span>{s.label}</span>
                  }
                </div>
                <div className="text-xs font-medium text-stone-400 uppercase tracking-widest mb-1">
                  {typeof s.value === 'number' ? s.label : ''}
                </div>
                <div className="text-xs text-stone-600">{s.desc}</div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════════════════════════════ ACTUALITÉS */}
      <CurrentEvents setCurrentSection={setCurrentSection} setActiveTab={setActiveTab} />

      {/* ═══════════════════════════════════ GALLERY */}
      <Gallery
        subtitle="Le club en images"
        images={galleryImages}
      />

      {/* ════════════════════════════ POURQUOI BCE */}
      <section className="section bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <ScrollReveal direction="left" className="lg:sticky top-28">
              <span className="eyebrow mb-4 block">Pourquoi nous choisir</span>
              <h2 className="display-lg mb-5">
                Un club pensé pour<br />votre{' '}
                <span className="text-brand-800">épanouissement</span>
              </h2>
              <p className="text-stone-500 text-base leading-relaxed mb-8 max-w-sm">
                Depuis 1998, nous accueillons joueurs de tous niveaux dans une
                ambiance chaleureuse et professionnelle.
              </p>
              <Link to="/inscription" onClick={() => setCurrentSection('inscription')} className="btn-brand">
                Rejoindre le club
              </Link>
            </ScrollReveal>

            <StaggerReveal className="space-y-1">
              {features.map((f, i) => (
                <FeatureItem key={i} {...f} />
              ))}
            </StaggerReveal>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ CTA BAND */}
      <section className="relative overflow-hidden" style={{ background: 'var(--gradient-brand)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #fff 0px, #fff 1px, transparent 1px, transparent 40px), repeating-linear-gradient(-45deg, #fff 0px, #fff 1px, transparent 1px, transparent 40px)',
        }} />
        <div className="absolute inset-0 bg-dot-grid opacity-[0.04]" />
        <ScrollReveal className="relative z-10 max-w-6xl mx-auto px-5 py-20 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tighter mb-4">Prêt à jouer ?</h2>
          <p className="text-white/70 text-lg mb-10 max-w-lg mx-auto">
            Rejoignez le Badminton Club Éragny{seasonLabel ? ` pour la saison ${seasonLabel}` : ''} et vivez une expérience sportive inoubliable.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            {inscriptionsOpen && (
              <a href="https://adherer.ffbad.club/bce95" target="_blank" rel="noopener noreferrer"
                onClick={trackInscriptionClick}
                className="btn bg-white text-brand-800 px-8 py-3.5 font-semibold hover:bg-white/90 shadow-xl">
                S'inscrire maintenant
              </a>
            )}
            <Link to="/contact" onClick={() => setCurrentSection('contact')}
              className="btn border-2 border-white/30 text-white px-8 py-3.5 font-semibold hover:bg-white/10">
              Nous contacter
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* ══════════════════════════════ PARTENAIRES */}
      <Partenaires />
    </div>
  );
};

export default Accueil;
